<template>
  <div>

    <div class="filter-grid mb-3">
      <DateRangeFilter v-model="dateRangeFilter" label="Transfer Date" />
    </div>

    <div v-if="loading" class="text-center p-3">
      <i class="fa fa-spinner fa-spin"></i>
      Loading transfer history...
    </div>

    <div v-else-if="error" class="text-center text-danger p-3">
      <i class="fa fa-exclamation-triangle"></i>
      Error loading transfer history: {{ error }}
    </div>

    <div v-else-if="!filteredTransfers.length" class="text-center text-muted p-3">
      <i class="fa fa-info-circle"></i>
      <template v-if="!rxTransfers.length">
        No transfer history found for this patient.
      </template>
      <template v-else>
        No transfers found matching the selected date range.
      </template>
    </div>

    <div v-else>
      <b-table ref="transferTable" show-empty sort-icon-left no-provider-sorting
               empty-text="No Transfers Found" empty-filtered-text="No Transfers Found"
               :fields="tableFields" :items="filteredTransfers"
               :per-page="currentPageSize" :current-page="pageNumber"
               :sort-by.sync="sortBy" :sort-desc.sync="sortDesc"
               small selectable select-mode="single"
               @row-selected="onRowSelected">

        <template #cell(inbound)="data">
            {{ data.value ? 'Inbound' : 'Outbound' }}
        </template>

        <template #cell(transferStatus)="data">
          <div>{{ getStatusText(data.value) }}</div>
          <small v-if="data.value === 2 && data.item.transferStatusReason" class="text-muted">
            {{ data.item.transferStatusReason }}
          </small>
        </template>
      </b-table>

      <BottomPagerBar v-if="filteredTransfers.length > currentPageSize"
                      v-model="pageNumber"
                      :total-rows="filteredTransfers.length"
                      :per-page="currentPageSize"
                      :show-page-size-selector="true"
                      :page-size-options="[5, 10, 15]"
                      item-name="transfer"
                      show-total
                      @change-page-size="currentPageSize = $event" />
    </div>

    <b-modal id="rxTransferHistoryDetailModal"
             size="lg"
             title="Transfer Details"
             hide-footer>
      <RxTransferModalComponent v-if="selectedTransferId"
                                :TransferIdDetails="selectedTransferId"
                                :patient="patient"
                                @complete="$bvModal.hide('rxTransferHistoryDetailModal')" />
    </b-modal>
  </div>
</template>

<script lang="ts">
import { Component, Prop, Vue, Watch, Ref } from 'vue-property-decorator';
import { Patient } from '@/models/Patient';
import { RxTransfer, transferStatus } from '@/models/RxTransfer';
import RxTransferModalComponent from '@/components/RxTransfer/RxTransferModalComponent.vue';
import DateRangeFilter from '@/components/DateRangeFilter.vue';
import BottomPagerBar from '@/components/BottomPagerBar.vue';
import { BvTableFieldArray, BTable } from 'bootstrap-vue';
import Axios from 'axios';
import moment from 'moment';

@Component({
  components: {
    RxTransferModalComponent,
    DateRangeFilter,
    BottomPagerBar
  }
})
export default class RxTransferHistoryComponent extends Vue {
  @Prop({ required: true }) patient!: Patient;
  @Prop({ default: 5 }) pageSize!: number;

  @Ref('transferTable') private transferTable!: BTable;

  private rxTransfers: RxTransfer[] = [];
  private loading = false;
  private error = '';
  private selectedTransferId: number | null = null;

  // Pagination
  private pageNumber = 1;
  private currentPageSize = 5;
  private sortBy = 'timeStamp';
  private sortDesc = true;
  // Filtering
  private dateRangeFilter: { startDate: any, endDate: any } = {
    startDate: null,
    endDate: null
  };

  get tableFields(): BvTableFieldArray {
    return [
      { key: 'timeStamp', label: 'Date', sortable: true, class: 'text-nowrap', formatter: this.formatDate },
      { key: 'inbound', label: 'Type', sortable: true },
      { key: 'transferStatus', label: 'Status', sortable: true },
      { key: 'drugName', label: 'Drug', sortable: true },
      { key: 'programName', label: 'Program', sortable: true }
    ];
  }

  get filteredTransfers(): RxTransfer[] {
    let transfers = [...this.rxTransfers];

    // Apply date range filter
    if (this.dateRangeFilter?.startDate) {
      transfers = transfers.filter(transfer => {
        const transferDate = new Date(transfer.timeStamp);
        const startDate = moment(this.dateRangeFilter.startDate).toDate();
        const endDate = moment(this.dateRangeFilter.endDate).add(1, 'day').toDate();
        return transferDate >= startDate && transferDate < endDate;
      });
    }

    return transfers;
  }

  mounted() {
    this.currentPageSize = this.pageSize;
    this.getFinishedRxTransfers();
  }

  @Watch('patient')
  onPatientChanged() {
    this.pageNumber = 1;
    this.getFinishedRxTransfers();
  }

  @Watch('dateRangeFilter', { deep: true })
  onDateFilterChanged() {
    this.pageNumber = 1;
  }

  async getFinishedRxTransfers(): Promise<RxTransfer[]> {
    const patientId = this.patient?.id;
    if (!patientId) return [];

    this.loading = true;
    this.error = '';

    try {
      const res = await Axios.get(`/RxTransfer/${patientId}/GetHistoryByPatient`);
      this.rxTransfers = res.data || [];
      return res.data;
    } catch (err) {
      console.error('error getting finished rx transfers', err);
      this.error = 'Failed to load transfer history';
      return [];
    } finally {
      this.loading = false;
    }
  }

  onRowSelected(items: RxTransfer[]) {
    if (items.length > 0) {
      this.viewTransferDetails(items[0].id);
    }
  }

  viewTransferDetails(transferId: any) {
    this.selectedTransferId = transferId;
    this.$bvModal.show('rxTransferHistoryDetailModal');
  }

  getStatusText(status: number): string {
    return transferStatus[status] || 'Unknown';
  }

  formatDate(dateString: string): string {
    return moment.utc(dateString).format('M/D/YY');
  }
}
</script>

<style scoped>
.filter-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  align-items: end;
}

@media (min-width: 768px) {
  .filter-grid {
    grid-template-columns: auto 1fr;
  }
}

.table tbody tr:hover {
  background-color: #f8f9fa;
}

.table tbody tr.table-active {
  background-color: #e3f2fd;
}
</style>
